import { createSignal } from 'solid-js';
import { fetchGroupedRenewalTemplates } from '~/contexts/leases/LeasesRenewalsContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseSettingsRepository } from '~/repositories/settings/leaseSettingsRepository';
import type { LeaseRenewalSettings, LeaseRenewalSettingsCompanyPortalTypedSettingsWithOverridesDocument } from '~/swagger/settings/Api';
export const [LeaseSettingsProvider, useLeaseSettings] = createMagicDoorContext('LeaseSettings', () => {
  const repo = new LeaseSettingsRepository();

  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [allLeaseSettings, setAllLeaseSettings] = createSignal<
    LeaseRenewalSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
  >();
  const [leaseTemplateOptions, setLeaseTemplateOptions] = createSignal<any[]>([]);

  const getAllLeaseSettings = async () => {
    try {
      setIsLoading(true);
      const json = await repo.getAllLeaseSettings();
      setAllLeaseSettings(json);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateLeaseSettings = async (payload: LeaseRenewalSettings) => {
    try {
      setIsLoading(true);
      await repo.updateLeaseSettings(payload);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const updateOverrideLeaseSettings = async (level: string, levelId: string, payload: LeaseRenewalSettings) => {
    try {
      setIsLoading(true);
      await repo.updateLeaseSettingsByLevel(level, levelId, payload);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const addOverrideSettings = async (level: string, levelId: string, payload: LeaseRenewalSettings) => {
    try {
      setIsLoading(true);
      await repo.updateLeaseSettingsByLevel(level, levelId, payload);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const removeOverrideSettings = async (level: string, levelId: string) => {
    try {
      setIsLoading(true);
      await repo.deleteLeaseSettingsByLevel(level, levelId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };
  const getLeaseTemplateOptions = async () => {
    const groupedLeaseTemplates = await fetchGroupedRenewalTemplates();

    const options: any[] = [];

    groupedLeaseTemplates.forEach((category) => {
      options.push({
        type: 'group',
        label: category.name,
      });

      category.templates.forEach((template) => {
        options.push({
          type: 'option',
          label: template.name,
          value: `${template.kind}:${template.id}`,
        });
      });
    });

    setLeaseTemplateOptions(options);
  };

  return {
    error,
    isLoading,
    allLeaseSettings,
    getAllLeaseSettings,
    updateLeaseSettings,
    addOverrideSettings,
    updateOverrideLeaseSettings,
    removeOverrideSettings,
    leaseTemplateOptions,
    getLeaseTemplateOptions,
  };
});
